// screen: Delivery — distributions, purge, pre-warm, playback checker, CDN-only onboarding (Studio spec §4.11, §4.15;
// control-plane change `distributions`, m4-prewarm-webhooks). Every action is a documented API route (§12 rule 1).
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { CheckCircle2, Copy, Globe, RotateCcw, Trash2, XCircle } from 'lucide-react';
import type { ApiError } from '@viewstream/api-client';
import { fmtDateTime } from '@viewstream/i18n';
import {
  Badge, Button, Checkbox, ConfirmByNameDialog, EmptyState, ErrorState, Field, Input, Label, Select, Skeleton, Switch,
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@viewstream/ui';
import { appTitle } from '~/app/brand';
import { Protection } from '~/app/Protection';
import { DRMService } from '~/app/DRMService';
import { TenantPackaging } from '~/app/Packaging';
import { CdnPartners } from '~/app/CdnPartners';
import { Steering } from '~/app/Steering';
import { SteeringRules } from '~/app/SteeringRules';
import { Migration } from '~/app/Migration';
import { EdgeOptions } from '~/app/EdgeOptions';
import { TenantVodAds } from '~/app/VodAds';
import { ModeToggle } from '~/app/uiMode';
import { CacheRulesEditor, LogExportSettings, OriginSettings, PurgeDistribution, VersionHistory } from '~/app/DistributionAdvanced';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import {
  useChannelList, useCheck, useDeleteDistribution, useDistStatus, useDistributions, useJob, usePrewarm, usePrewarmRun, usePurge,
  useRotateSecret, useSaveDistribution, useSignURL, useTrafficSeen,
  type CheckResult, type Distribution, type DistributionInput, type TokenMode,
} from '~/app/delivery';

type Tab = 'onboarding' | 'distributions' | 'protection' | 'drm' | 'packaging' | 'ads' | 'partners' | 'steering' | 'rules' | 'migration' | 'purge' | 'prewarm' | 'checker';
const TABS: Tab[] = ['onboarding', 'distributions', 'protection', 'drm', 'packaging', 'ads', 'partners', 'steering', 'rules', 'migration', 'purge', 'prewarm', 'checker'];
export const Route = createFileRoute('/_layout/t/$slug/delivery')({
  component: Screen,
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } => (TABS.includes(s['tab'] as Tab) ? { tab: s['tab'] as Tab } : {}),
});

const errText = (e: unknown) => (e as ApiError)?.problem?.errors?.map((x) => `${x.field}: ${x.detail}`).join('; ') || (e as ApiError)?.problem?.detail || (e as Error)?.message;
const lines = (s: string) => s.split(/[\n,]+/).map((x) => x.trim()).filter(Boolean);
const verdictTone = (v: string): 'ok' | 'warn' | 'danger' | 'neutral' => (v === 'ok' ? 'ok' : v === 'skipped' ? 'neutral' : v === 'token_rejected' || v === 'geo_blocked' ? 'warn' : 'danger');

function Screen() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const cdn = tenant.mode === 'cdn';
  const tab: Tab = search.tab ?? (cdn ? 'onboarding' : 'distributions');
  React.useEffect(() => { document.title = appTitle(t`Delivery`); }, [t]);
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">{t`Delivery`}</h1>
      <Tabs value={tab} onValueChange={(v) => void navigate({ search: { tab: v as Tab }, replace: true })}>
        <TabsList aria-label={t`Delivery sections`} className="flex-wrap">
          {cdn ? <TabsTrigger value="onboarding">{t`Onboarding`}</TabsTrigger> : null}
          <TabsTrigger value="distributions">{t`Distributions`}</TabsTrigger>
          <TabsTrigger value="protection">{t`Protection`}</TabsTrigger>
          <TabsTrigger value="drm">{t`DRM service`}</TabsTrigger>
          {cdn ? null : <TabsTrigger value="packaging">{t`Packaging`}</TabsTrigger>}
          {cdn ? null : <TabsTrigger value="ads">{t`Library ads`}</TabsTrigger>}
          <TabsTrigger value="partners">{t`Additional CDNs`}</TabsTrigger>
          <TabsTrigger value="steering">{t`Traffic steering`}</TabsTrigger>
          <TabsTrigger value="rules">{t`Steering rules`}</TabsTrigger>
          <TabsTrigger value="migration">{t`Migration`}</TabsTrigger>
          <TabsTrigger value="purge">{t`Purge`}</TabsTrigger>
          <TabsTrigger value="prewarm">{t`Pre-warm`}</TabsTrigger>
          <TabsTrigger value="checker">{t`Playback checker`}</TabsTrigger>
        </TabsList>
        {cdn ? <TabsContent value="onboarding" className="pt-4"><Onboarding /></TabsContent> : null}
        <TabsContent value="distributions" className="pt-4"><Distributions /></TabsContent>
        <TabsContent value="protection" className="pt-4"><Protection /></TabsContent>
        <TabsContent value="drm" className="pt-4"><DRMService /></TabsContent>
        {cdn ? null : <TabsContent value="packaging" className="space-y-4 pt-4"><ModeToggle /><div className="max-w-3xl"><TenantPackaging /></div></TabsContent>}
        {cdn ? null : <TabsContent value="ads" className="pt-4"><TenantVodAds /></TabsContent>}
        <TabsContent value="partners" className="pt-4"><CdnPartners /></TabsContent>
        <TabsContent value="steering" className="pt-4"><Steering /></TabsContent>
        <TabsContent value="rules" className="pt-4"><SteeringRules /></TabsContent>
        <TabsContent value="migration" className="pt-4"><Migration /></TabsContent>
        <TabsContent value="purge" className="pt-4"><Purge /></TabsContent>
        <TabsContent value="prewarm" className="pt-4"><Prewarm /></TabsContent>
        <TabsContent value="checker" className="pt-4"><Checker /></TabsContent>
      </Tabs>
    </div>
  );
}

function RoleHint({ scope }: { scope: string }) {
  const { t } = useLingui();
  return <p className="rounded border border-border bg-surface-2 p-2 text-sm text-fg-muted" role="note">{t`Read-only: your role lacks the ${scope} permission (Engineer or above).`}</p>;
}

function Copyable({ value, label }: { value: string; label: string }) {
  const { t } = useLingui();
  return (
    <span className="inline-flex max-w-full items-center gap-1">
      <code className="ltr-isolate break-all text-xs">{value}</code>
      <Button variant="ghost" size="sm" aria-label={t`Copy ${label}`} onClick={() => { void navigator.clipboard?.writeText(value); }}><Copy className="size-3.5" aria-hidden /></Button>
    </span>
  );
}

/** The one-time secret box (create / rotate / token switch). */
function SecretOnce({ secret, onDone }: { secret: string; onDone: () => void }) {
  const { t } = useLingui();
  return (
    <div role="status" className="rounded border border-warn/30 bg-warn/10 p-2 text-sm" data-testid="secret-once">
      <p>{t`New token secret — shown once. Store it where your signing code runs:`}</p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <Copyable value={secret} label={t`secret`} />
        <Button variant="ghost" size="sm" onClick={onDone}>{t`Done`}</Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- distributions

function Distributions() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const q = useDistributions(tenant.slug);
  const [open, setOpen] = React.useState<string | 'new' | undefined>();
  const [secret, setSecret] = React.useState<string | undefined>();
  const write = can(scopes, 'delivery:write');
  if (q.isError) return <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load distributions`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  if (q.isPending) return <Skeleton className="h-32" />;
  const items = q.data.items;
  const current = items.find((d) => d.id === open);
  return (
    <div className="space-y-4">
      <p className="text-sm text-fg-muted">{t`A distribution is a hostname the ViewStream edges serve for you. Point its CNAME at ${q.data.cname_target}; the edges pick up changes on the next rules render and request a Let's Encrypt certificate once DNS points at them.`}</p>
      {!write ? <RoleHint scope="delivery:write" /> : <Button onClick={() => setOpen('new')}>{t`New distribution`}</Button>}
      {secret ? <SecretOnce secret={secret} onDone={() => setSecret(undefined)} /> : null}
      {open === 'new' ? <DistributionForm onCancel={() => setOpen(undefined)} onSaved={(d) => { setOpen(d.id); if (d.secret) setSecret(d.secret); }} /> : null}
      {items.length === 0 ? <EmptyState icon={<Globe className="size-8" aria-hidden />} title={t`No distributions yet`} /> : (
        <table className="w-full text-sm" data-testid="dist-table">
          <thead className="text-xs text-fg-muted">
            <tr><th className="text-start">{t`Hostname`}</th><th className="text-start">{t`Origin`}</th><th className="text-start">{t`Token`}</th><th className="text-start">{t`CORS`}</th><th className="text-start">{t`Geo`}</th><th className="text-start">{t`State`}</th></tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className="border-t border-border align-top">
                <td className="py-2">
                  <button type="button" className="text-primary underline-offset-2 hover:underline" onClick={() => setOpen(open === d.id ? undefined : d.id)} aria-expanded={open === d.id}>
                    <span className="ltr-isolate">{d.hostname}</span>
                  </button>
                  {d.primary ? <Badge tone="primary" className="ms-2">{t`primary`}</Badge> : null}
                </td>
                <td><span className="ltr-isolate text-xs">{d.origin_kind === 'library' ? t`ViewStream library + live` : d.origin_url}</span></td>
                <td><Badge tone={d.token_mode === 'none' ? 'neutral' : 'ok'}>{d.token_mode.toUpperCase()}</Badge></td>
                <td className="text-xs">{d.cors_origins.length ? d.cors_origins.length : '*'}</td>
                <td className="text-xs ltr-isolate">{d.geo_allow.length ? `+${d.geo_allow.join(',')}` : ''}{d.geo_deny.length ? ` −${d.geo_deny.join(',')}` : ''}{!d.geo_allow.length && !d.geo_deny.length ? '—' : ''}</td>
                <td>{d.enabled ? <Badge tone="ok">{t`enabled`}</Badge> : <Badge>{t`disabled`}</Badge>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {current ? <DistributionDetail key={current.id} d={current} write={write} onClose={() => setOpen(undefined)} /> : null}
    </div>
  );
}

function DistributionForm({ d, onSaved, onCancel }: { d?: Distribution; onSaved: (d: Distribution) => void; onCancel?: () => void }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const save = useSaveDistribution(tenant.slug);
  const [hostname, setHostname] = React.useState(d?.hostname ?? '');
  const [kind, setKind] = React.useState<Distribution['origin_kind']>(d?.origin_kind ?? (tenant.mode === 'cdn' ? 'external' : 'library'));
  const [originURL, setOriginURL] = React.useState(d?.origin_url ?? '');
  const [hostHeader, setHostHeader] = React.useState(d?.origin_host_header ?? '');
  const [token, setToken] = React.useState<TokenMode>(d?.token_mode ?? 'none');
  const [cors, setCors] = React.useState((d?.cors_origins ?? []).join('\n'));
  const [geoAllow, setGeoAllow] = React.useState((d?.geo_allow ?? []).join(', '));
  const [geoDeny, setGeoDeny] = React.useState((d?.geo_deny ?? []).join(', '));
  const [qk, setQk] = React.useState((d?.cache_rules.query_keys ?? []).join(', '));
  const [enabled, setEnabled] = React.useState(d?.enabled ?? true);
  const [err, setErr] = React.useState<string | undefined>();
  const id = d?.id ?? 'new';
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(undefined);
    const body: DistributionInput = {
      origin_kind: kind, token_mode: token, cors_origins: lines(cors), geo_allow: lines(geoAllow), geo_deny: lines(geoDeny),
      cache_rules: { ...(d?.cache_rules ?? {}), query_keys: lines(qk) }, enabled,
    };
    if (!d) body.hostname = hostname.trim();
    if (kind === 'external') { body.origin_url = originURL.trim(); body.origin_host_header = hostHeader.trim(); }
    save.mutate({ id: d?.id, body }, { onSuccess: onSaved, onError: (x) => setErr(errText(x)) });
  };
  return (
    <form onSubmit={submit} className="space-y-1 rounded-lg border border-border p-3" aria-label={d ? t`Distribution settings` : t`New distribution`}>
      {!d ? (
        <Field id={`${id}-host`} label={t`Hostname`} hint={t`e.g. cdn.example.com — you will point its CNAME at the ViewStream edge.`}>
          <Input required dir="ltr" value={hostname} onChange={(e) => setHostname(e.target.value)} placeholder="cdn.example.com" />
        </Field>
      ) : null}
      {tenant.mode === 'platform' ? (
        <div className="mb-4">
          <Label htmlFor={`${id}-kind`}>{t`Origin`}</Label>
          <Select ariaLabel={t`Origin`} value={kind} onValueChange={(v) => setKind(v as Distribution['origin_kind'])}
            options={[{ value: 'library', label: t`ViewStream library + live` }, { value: 'external', label: t`My own origin` }]} />
        </div>
      ) : null}
      {kind === 'external' ? (
        <>
          <Field id={`${id}-origin`} label={t`Origin URL`} hint={t`http(s)://host[:port] — the request path is passed through unchanged.`}>
            <Input required dir="ltr" value={originURL} onChange={(e) => setOriginURL(e.target.value)} placeholder="https://origin.example.com" />
          </Field>
          <Field id={`${id}-hh`} label={t`Host header sent to the origin`} hint={t`Empty = the origin URL's host.`}>
            <Input dir="ltr" value={hostHeader} onChange={(e) => setHostHeader(e.target.value)} />
          </Field>
        </>
      ) : null}
      <div className="mb-4">
        <Label htmlFor={`${id}-token`}>{t`Token mode`}</Label>
        <Select ariaLabel={t`Token mode`} value={token} onValueChange={(v) => setToken(v as TokenMode)}
          options={[{ value: 'none', label: t`None (public)` }, { value: 'hmac', label: t`ViewStream HMAC (path prefix)` }, { value: 'jwt', label: t`JWT (?t= or cookie)` }]} />
      </div>
      <div className="mb-4">
        <Label htmlFor={`${id}-cors`}>{t`CORS allow-list`}</Label>
        <textarea id={`${id}-cors`} dir="ltr" rows={2} value={cors} onChange={(e) => setCors(e.target.value)} placeholder="https://www.example.com"
          aria-describedby={`${id}-cors-hint`} className="w-full rounded-md border border-border bg-surface p-2 text-sm" />
        <p id={`${id}-cors-hint`} className="mt-1 text-xs text-fg-muted">{t`One origin per line. Empty = any origin (Access-Control-Allow-Origin: *).`}</p>
      </div>
      <div className="grid gap-x-4 sm:grid-cols-3">
        <Field id={`${id}-ga`} label={t`Allow countries`} hint={t`ISO codes, e.g. IL. Empty = all.`}>
          <Input dir="ltr" value={geoAllow} onChange={(e) => setGeoAllow(e.target.value)} />
        </Field>
        <Field id={`${id}-gd`} label={t`Deny countries`}>
          <Input dir="ltr" value={geoDeny} onChange={(e) => setGeoDeny(e.target.value)} />
        </Field>
        <Field id={`${id}-qk`} label={t`Query keys in the cache key`} hint={t`Tokens never fragment the cache.`}>
          <Input dir="ltr" value={qk} onChange={(e) => setQk(e.target.value)} />
        </Field>
      </div>
      <label className="mb-3 flex items-center gap-2 text-sm">
        <Switch checked={enabled} onCheckedChange={setEnabled} disabled={d?.primary} aria-label={t`Enabled`} /> {t`Enabled`}
      </label>
      {err ? <p role="alert" className="text-sm text-danger">{err}</p> : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>{d ? t`Save` : t`Create distribution`}</Button>
        {onCancel ? <Button type="button" variant="ghost" onClick={onCancel}>{t`Cancel`}</Button> : null}
      </div>
    </form>
  );
}

function StatusPanel({ d }: { d: Distribution }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale, tz } = useLocale();
  const st = useDistStatus(tenant.slug, d.id);
  if (st.isPending) return <Skeleton className="h-16" />;
  if (st.isError) return <p className="text-sm text-danger">{t`Status unavailable`}</p>;
  const s = st.data;
  return (
    <div className="space-y-2 text-sm" data-testid="dist-status">
      <p className="flex flex-wrap items-center gap-2">
        {s.dns.points_at_edge ? <CheckCircle2 className="size-4 text-ok" aria-hidden /> : <XCircle className="size-4 text-warn" aria-hidden />}
        {s.dns.points_at_edge ? t`DNS points at the edge` : t`DNS does not point at the edge yet`}
        <span className="ltr-isolate text-xs text-fg-muted">{s.dns.addresses.join(', ') || '—'}</span>
      </p>
      <table className="text-xs">
        <thead className="text-fg-muted"><tr><th className="pe-4 text-start">{t`Edge`}</th><th className="pe-4 text-start">{t`TLS`}</th><th className="pe-4 text-start">{t`Issuer`}</th><th className="text-start">{t`Expires`}</th></tr></thead>
        <tbody>
          {s.tls.map((e) => (
            <tr key={e.edge}>
              <td className="pe-4">{e.edge} <span className="text-fg-muted">({e.site})</span></td>
              <td className="pe-4">{e.ok ? <Badge tone="ok">{t`valid`}</Badge> : <Badge tone="warn" title={e.error}>{t`not valid`}</Badge>}</td>
              <td className="pe-4 ltr-isolate">{e.issuer ?? '—'}</td>
              <td>{e.not_after ? `${fmtDateTime(e.not_after, { locale, tz }, { dateStyle: 'medium' })} (${t`${e.days_left ?? 0} days`})` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DistributionDetail({ d, write, onClose }: { d: Distribution; write: boolean; onClose: () => void }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const rotate = useRotateSecret(tenant.slug);
  const del = useDeleteDistribution(tenant.slug);
  const sign = useSignURL();
  const [secret, setSecret] = React.useState<string | undefined>();
  const [overlap, setOverlap] = React.useState('3600');
  const [deleting, setDeleting] = React.useState(false);
  const [path, setPath] = React.useState(tenant.mode === 'cdn' ? '/live/sim/master.m3u8' : `/vod/${tenant.slug}/`);
  const [signed, setSigned] = React.useState<string | undefined>();
  const [err, setErr] = React.useState<string | undefined>();
  return (
    <section aria-label={d.hostname} className="space-y-4 rounded-lg border border-border p-4" data-testid="dist-detail">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold ltr-isolate">{d.hostname}</h2>
        <Button variant="ghost" size="sm" onClick={onClose}>{t`Close`}</Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <h3 className="font-medium">{t`DNS and TLS`}</h3>
          <p className="text-sm">{t`CNAME target:`} <Copyable value={d.cname_target} label={t`CNAME target`} /></p>
          {d.origin_kind === 'external' ? <p className="text-sm">{t`Allow these edge addresses at your origin:`} <Copyable value={d.edge_ips.join(' ')} label={t`edge addresses`} /></p> : null}
          <StatusPanel d={d} />
        </div>
        <div className="space-y-2">
          <h3 className="font-medium">{t`Token secret`}</h3>
          {d.token_mode === 'none' ? <p className="text-sm text-fg-muted">{t`No token: the distribution is public.`}</p> : (
            <>
              <p className="text-sm">{t`Current secret:`} <code className="ltr-isolate">{d.secret_hint ?? '—'}</code>
                {d.prev_secret_active ? <Badge tone="warn" className="ms-2">{t`previous secret still accepted`}</Badge> : null}</p>
              {secret ? <SecretOnce secret={secret} onDone={() => setSecret(undefined)} /> : null}
              {write ? (
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <Label htmlFor={`${d.id}-ov`}>{t`Overlap`}</Label>
                    <Select ariaLabel={t`Overlap`} value={overlap} onValueChange={setOverlap}
                      options={[{ value: '0', label: t`none — old tokens stop now` }, { value: '600', label: t`10 minutes` }, { value: '3600', label: t`1 hour` }, { value: '86400', label: t`1 day` }]} />
                  </div>
                  <Button variant="secondary" disabled={rotate.isPending}
                    onClick={() => rotate.mutate({ id: d.id, overlap_s: Number(overlap) }, { onSuccess: (x) => setSecret(x.secret), onError: (x) => setErr(errText(x)) })}>
                    <RotateCcw className="size-4" aria-hidden /> {t`Rotate secret`}
                  </Button>
                </div>
              ) : null}
              {write ? (
                <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); sign.mutate({ id: d.id, path, expires_in_s: 3600, scope: 'dir' }, { onSuccess: (x) => setSigned(x.url), onError: (x) => setErr(errText(x)) }); }}>
                  <Field id={`${d.id}-sign`} label={t`Sign a URL (valid 1 hour, covers the path's directory)`}>
                    <Input dir="ltr" value={path} onChange={(e) => setPath(e.target.value)} />
                  </Field>
                  <Button type="submit" variant="secondary" disabled={sign.isPending}>{t`Sign`}</Button>
                  {signed ? <p className="text-sm" data-testid="signed-url"><Copyable value={signed} label={t`signed URL`} /></p> : null}
                </form>
              ) : null}
            </>
          )}
        </div>
      </div>
      {err ? <p role="alert" className="text-sm text-danger">{err}</p> : null}
      <h3 className="font-medium">{t`Settings`}</h3>
      {write ? <DistributionForm d={d} onSaved={(x) => { if (x.secret) setSecret(x.secret); }} /> : <RoleHint scope="delivery:write" />}
      <div className="space-y-2">
        <OriginSettings d={d} write={write} />
        <CacheRulesEditor d={d} write={write} />
        <PurgeDistribution d={d} write={write} />
        <LogExportSettings d={d} write={write} />
        <EdgeOptions d={d} write={write} />
        <VersionHistory d={d} write={write} onSecret={setSecret} />
        <details className="rounded-lg border border-border p-3" data-testid="dist-checker">
          <summary className="cursor-pointer font-medium">{t`Check playback from every site`}</summary>
          <div className="mt-3"><Checker initialURL={`https://${d.hostname}${tenant.mode === 'cdn' ? '/live/sim/master.m3u8' : `/vod/${tenant.slug}/`}`} /></div>
        </details>
      </div>
      {write && !d.primary ? (
        <Button variant="ghost" onClick={() => setDeleting(true)}><Trash2 className="size-4" aria-hidden /> {t`Delete distribution`}</Button>
      ) : null}
      {deleting ? (
        <ConfirmByNameDialog open onOpenChange={(o) => { if (!o) setDeleting(false); }} name={d.hostname}
          title={t`Delete distribution`} description={t`The edges stop serving this hostname on the next rules render. Type the hostname to confirm.`}
          labels={{ field: t`Hostname`, confirm: t`Delete`, cancel: t`Cancel`, close: t`Close` }} busy={del.isPending}
          onConfirm={() => del.mutateAsync(d.id).then(() => { setDeleting(false); onClose(); })} />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------- purge

function EdgeResults({ rows }: { rows: { edge: string; cells: React.ReactNode[]; error?: string | null }[] }) {
  const { t } = useLingui();
  return (
    <ul className="space-y-1 text-sm" data-testid="edge-results">
      {rows.map((r) => (
        <li key={r.edge} className="flex flex-wrap items-center gap-3 rounded border border-border px-2 py-1">
          <span className="font-medium">{r.edge}</span>
          {r.cells}
          {r.error ? <span className="text-danger">{t`error:`} <span className="ltr-isolate">{r.error}</span></span> : null}
        </li>
      ))}
    </ul>
  );
}

function Purge() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const purge = usePurge();
  const dists = useDistributions(tenant.slug);
  const [mode, setMode] = React.useState<'urls' | 'prefix' | 'asset_id' | 'clip_id' | 'distribution_id' | 'tags'>('urls');
  const [value, setValue] = React.useState('');
  const [jobId, setJobId] = React.useState<string | undefined>();
  const [generation, setGeneration] = React.useState<string | undefined>();
  const [err, setErr] = React.useState<string | undefined>();
  const job = useJob(jobId);
  const write = can(scopes, 'delivery:write');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(undefined);
    setJobId(undefined);
    setGeneration(undefined);
    const body = mode === 'urls' || mode === 'tags' ? { [mode]: lines(value) } : { [mode]: value.trim() };
    purge.mutate(body, {
      onSuccess: (r) => (r.job_id ? setJobId(r.job_id) : setGeneration(t`${r.hostname ?? ''}: everything cached is dropped within about 10 seconds (generation ${r.cache_generation ?? ''}).`)),
      onError: (x) => setErr(errText(x)),
    });
  };
  const opts = [{ value: 'urls', label: t`URLs` }, { value: 'prefix', label: t`Path prefix` }];
  if (tenant.mode === 'platform') opts.push({ value: 'asset_id', label: t`Asset` }, { value: 'clip_id', label: t`Clip` });
  opts.push({ value: 'distribution_id', label: t`Whole distribution` }, { value: 'tags', label: t`Cache tags` });
  const done = job.data && ['succeeded', 'failed', 'cancelled'].includes(job.data.status);
  return (
    <div className="space-y-3">
      <p className="text-sm text-fg-muted">{t`Drops cached copies on every edge; the next request is fetched from the origin. URLs may be on any of your distribution hostnames (signed URLs are fine — the token is not part of the cache key).`}</p>
      {!write ? <RoleHint scope="delivery:write" /> : null}
      <form onSubmit={submit} className="space-y-2">
        <fieldset disabled={!write} className="space-y-2">
          <div className="max-w-xs">
            <Label htmlFor="purge-mode">{t`Purge by`}</Label>
            <Select ariaLabel={t`Purge by`} value={mode} onValueChange={(v) => { setMode(v as typeof mode); setValue(''); }} options={opts} />
          </div>
          {mode === 'distribution_id' ? (
            <div className="max-w-sm">
              <Label htmlFor="purge-dist">{t`Distribution`}</Label>
              <Select ariaLabel={t`Distribution`} value={value} onValueChange={setValue} placeholder={t`Choose a distribution`}
                options={(dists.data?.items ?? []).map((x) => ({ value: x.id, label: x.hostname }))} />
            </div>
          ) : (
            <>
              <Label htmlFor="purge-value">{mode === 'urls' ? t`URLs (one per line)` : mode === 'tags' ? t`Cache tags (one per line; needs "Record cache tags" in the distribution's cache rules)` : mode === 'prefix' ? t`Prefix, e.g. /vod/${tenant.slug}/<asset>/` : t`Id`}</Label>
              <textarea id="purge-value" dir="ltr" rows={mode === 'urls' || mode === 'tags' ? 4 : 1} value={value} onChange={(e) => setValue(e.target.value)} required
                className="w-full rounded-md border border-border bg-surface p-2 font-mono text-xs" />
            </>
          )}
          <Button type="submit" disabled={purge.isPending || !value.trim()}>{t`Purge`}</Button>
        </fieldset>
      </form>
      {err ? <p role="alert" className="text-sm text-danger">{err}</p> : null}
      {generation ? <p role="status" className="text-sm text-ok">{generation}</p> : null}
      {jobId ? (
        <div role="status" aria-live="polite" className="space-y-2">
          <p className="text-sm">{done ? (job.data?.status === 'succeeded' ? t`Purge finished.` : t`Purge ${job.data?.status ?? ''}: ${job.data?.error ?? ''}`) : t`Purging on every edge…`}</p>
          {job.data?.result?.edges ? (
            <EdgeResults rows={job.data.result.edges.map((e) => ({ edge: e.edge, error: e.error, cells: [
              <span key="k">{t`${e.keys} keys`}</span>, <Badge key="p" tone="ok">{t`${e.purged} purged`}</Badge>, <span key="m" className="text-fg-muted">{t`${e.missing} not cached`}</span>] }))} />
          ) : done ? <p className="text-sm text-fg-muted">{t`No edge keys matched.`}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- pre-warm

function Prewarm() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const platform = tenant.mode === 'platform';
  const prewarm = usePrewarm();
  const channels = useChannelList(tenant.slug, platform);
  const [mode, setMode] = React.useState<'urls' | 'asset_id' | 'channel_id'>('urls');
  const [value, setValue] = React.useState('');
  const [runId, setRunId] = React.useState<string | undefined>();
  const [err, setErr] = React.useState<string | undefined>();
  const run = usePrewarmRun(runId);
  const allowed = can(scopes, 'prewarm');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(undefined);
    setRunId(undefined);
    const body = mode === 'urls' ? { urls: lines(value) } : { [mode]: value.trim() };
    prewarm.mutate(body, { onSuccess: (r) => setRunId(r.id), onError: (x) => setErr(errText(x)) });
  };
  const opts = [{ value: 'urls', label: t`URL list` }];
  if (platform) opts.push({ value: 'asset_id', label: t`Asset` }, { value: 'channel_id', label: t`Channel` });
  const chItems = channels.data?.items ?? [];
  return (
    <div className="space-y-3">
      <p className="text-sm text-fg-muted">{platform ? t`Fetches the playlists and first segments through every edge before viewers arrive (6 runs per minute).` : t`CDN-only tenants pre-warm a URL list — typically called by your CMS on publish (6 runs per minute).`}</p>
      {!allowed ? <RoleHint scope="prewarm" /> : null}
      <form onSubmit={submit} className="space-y-2">
        <fieldset disabled={!allowed} className="space-y-2">
          <div className="max-w-xs">
            <Label htmlFor="pw-mode">{t`Pre-warm`}</Label>
            <Select ariaLabel={t`Pre-warm`} value={mode} onValueChange={(v) => { setMode(v as typeof mode); setValue(''); }} options={opts} />
          </div>
          {mode === 'channel_id' ? (
            <div className="max-w-xs">
              <Label htmlFor="pw-ch">{t`Channel`}</Label>
              <Select ariaLabel={t`Channel`} value={value} onValueChange={setValue} options={chItems.map((c) => ({ value: c.id, label: c.title || c.slug }))} placeholder={t`Choose a channel`} />
            </div>
          ) : (
            <>
              <Label htmlFor="pw-value">{mode === 'urls' ? t`URLs (one per line, on your distribution hostname)` : t`Asset id`}</Label>
              <textarea id="pw-value" dir="ltr" rows={mode === 'urls' ? 4 : 1} value={value} onChange={(e) => setValue(e.target.value)} required
                className="w-full rounded-md border border-border bg-surface p-2 font-mono text-xs" />
            </>
          )}
          <Button type="submit" disabled={prewarm.isPending || !value.trim()}>{t`Start pre-warm`}</Button>
        </fieldset>
      </form>
      {err ? <p role="alert" className="text-sm text-danger">{err}</p> : null}
      {runId ? (
        <div role="status" aria-live="polite" className="space-y-2">
          <p className="text-sm">{t`Run status: ${run.data?.status ?? '…'}`} {run.data?.urls ? t`(${run.data.urls.length} URLs planned)` : null}</p>
          {run.data?.sites?.length ? (
            <ul className="flex flex-wrap gap-2 text-sm" data-testid="prewarm-sites">
              {run.data.sites.map((s) => (
                <li key={s.site || '-'} className="flex items-center gap-2 rounded border border-border px-2 py-1">
                  <span className="font-medium">{s.site || t`unknown site`}</span>
                  <Badge tone={s.complete ? 'ok' : s.failed ? 'danger' : 'neutral'}>{s.complete ? t`complete` : t`incomplete`}</Badge>
                  <span className="text-fg-muted">{t`${s.edges} edges, ${s.ok} ok, ${s.failed} failed`}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {run.data?.results?.length ? (
            <EdgeResults rows={run.data.results.map((e) => ({ edge: e.edge, error: e.errors?.[0], cells: [
              <Badge key="o" tone="ok">{t`${e.ok} ok`}</Badge>, <Badge key="f" tone={e.failed ? 'danger' : 'neutral'}>{t`${e.failed} failed`}</Badge>,
              <span key="h" className="text-fg-muted">{t`${e.hits} already cached`}</span>, <span key="ms" className="text-fg-muted">{t`${e.ms} ms`}</span>] }))} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- checker

function CheckResults({ r }: { r: CheckResult }) {
  const { t } = useLingui();
  const stepLabel = (s: string) => (s === 'master' ? t`Master playlist` : s === 'media' ? t`Media playlist` : t`Segment`);
  const verdictLabel = (v: string) => (v === 'ok' ? t`OK` : v === 'token_rejected' ? t`Token rejected` : v === 'geo_blocked' ? t`Geo blocked` : v === 'not_found' ? t`Not found` : v === 'skipped' ? t`Skipped` : v);
  return (
    <div className="space-y-3" data-testid="check-results">
      <p className="text-sm">
        <Badge tone={verdictTone(r.verdict)} data-testid="check-verdict">{verdictLabel(r.verdict)}</Badge>{' '}
        {r.signed ? t`Checked with a signed URL:` : t`Checked:`} <span className="ltr-isolate break-all text-xs">{r.url}</span>
      </p>
      {r.edges.map((e) => (
        <div key={e.edge} className="overflow-x-auto rounded border border-border p-2">
          <p className="mb-1 text-sm font-medium">{e.edge} <span className="text-fg-muted">({e.site})</span> <Badge tone={verdictTone(e.verdict)}>{verdictLabel(e.verdict)}</Badge></p>
          <table className="w-full text-xs">
            <thead className="text-fg-muted"><tr><th className="text-start">{t`Step`}</th><th className="text-start">{t`Status`}</th><th className="text-start">{t`Time`}</th><th className="text-start">{t`Cache`}</th><th className="text-start">{t`CORS`}</th><th className="text-start">{t`Range`}</th><th className="text-start">{t`Result`}</th></tr></thead>
            <tbody>
              {e.steps.map((s) => (
                <tr key={s.step} className="border-t border-border align-top">
                  <td className="py-1" title={s.url}>{stepLabel(s.step)}</td>
                  <td>{s.status || '—'}</td>
                  <td>{t`${s.ms} ms`}</td>
                  <td>{s.cache || '—'}</td>
                  <td className="ltr-isolate">{s.cors || '—'}</td>
                  <td className="ltr-isolate">{s.range || '—'}</td>
                  <td><Badge tone={verdictTone(s.verdict)}>{verdictLabel(s.verdict)}</Badge>{s.note || s.error ? <span className="ms-1 text-fg-muted">{s.note ?? s.error}</span> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function Checker({ initialURL }: { initialURL?: string }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const dists = useDistributions(tenant.slug);
  const check = useCheck();
  const [url, setUrl] = React.useState(initialURL ?? '');
  const [origin, setOrigin] = React.useState('');
  const [signIt, setSignIt] = React.useState(true);
  const [err, setErr] = React.useState<string | undefined>();
  React.useEffect(() => {
    if (!url && dists.data?.items[0]) setUrl(`https://${dists.data.items[0].hostname}${tenant.mode === 'cdn' ? '/live/sim/master.m3u8' : `/vod/${tenant.slug}/`}`);
  }, [dists.data, url, tenant]);
  let host = '';
  try { host = new URL(url).hostname; } catch { /* typing */ }
  const dist = dists.data?.items.find((d) => d.hostname === host);
  const tokenised = !!dist && dist.token_mode !== 'none';
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(undefined);
    check.mutate({ url: url.trim(), sign: tokenised && signIt, origin: origin.trim() || undefined }, { onError: (x) => setErr(errText(x)) });
  };
  return (
    <div className="space-y-3">
      <p className="text-sm text-fg-muted">{t`Plays a URL through every edge from the control plane: the playlist, its first variant and one segment (with a byte range). Reports status, time, cache, CORS and token results.`}</p>
      <form onSubmit={submit} className="space-y-1">
        <Field id="chk-url" label={t`Manifest or object URL`}>
          <Input required dir="ltr" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://cdn.example.com/live/ch1/master.m3u8" />
        </Field>
        <Field id="chk-origin" label={t`Test CORS for page origin (optional)`}>
          <Input dir="ltr" value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="https://www.example.com" />
        </Field>
        {tokenised ? (
          <label className="mb-3 flex items-center gap-2 text-sm">
            <Checkbox checked={signIt} onCheckedChange={(v) => setSignIt(v === true)} aria-label={t`Sign the URL with the distribution's token`} />
            {t`Sign the URL with the distribution's ${dist.token_mode.toUpperCase()} token (untick to confirm unsigned requests are refused)`}
          </label>
        ) : null}
        <Button type="submit" disabled={check.isPending}>{check.isPending ? t`Checking…` : t`Check playback`}</Button>
      </form>
      {err ? <p role="alert" className="text-sm text-danger">{err}</p> : null}
      {check.data ? <CheckResults r={check.data} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------- CDN-only onboarding (§4.15)

function Onboarding() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  const dists = useDistributions(tenant.slug);
  const save = useSaveDistribution(tenant.slug);
  const [step, setStep] = React.useState(1);
  const [picked, setPicked] = React.useState<string | undefined>();
  const [secret, setSecret] = React.useState<string | undefined>();
  const [err, setErr] = React.useState<string | undefined>();
  const d = dists.data?.items.find((x) => x.id === picked) ?? dists.data?.items[0];
  const traffic = useTrafficSeen(tenant.slug, step === 5 ? d?.id : undefined, step === 5);
  const write = can(scopes, 'delivery:write');
  const steps = [t`Create distribution`, t`Check playback`, t`Token mode`, t`DNS and allow-list`, t`Switch traffic`];
  if (dists.isPending) return <Skeleton className="h-32" />;
  return (
    <div className="space-y-4">
      <ol className="flex flex-wrap gap-2" aria-label={t`Onboarding steps`}>
        {steps.map((s, i) => (
          <li key={s}>
            <button type="button" onClick={() => setStep(i + 1)} aria-current={step === i + 1 ? 'step' : undefined}
              className={`rounded-full border px-3 py-1 text-sm ${step === i + 1 ? 'border-primary bg-primary/10 text-primary' : 'border-border text-fg-muted'}`}>
              {i + 1}. {s}
            </button>
          </li>
        ))}
      </ol>
      {!write ? <RoleHint scope="delivery:write" /> : null}
      {dists.data && dists.data.items.length > 1 ? (
        <div className="max-w-sm">
          <Label htmlFor="ob-dist">{t`Distribution`}</Label>
          <Select ariaLabel={t`Distribution`} value={d?.id} onValueChange={setPicked} options={dists.data.items.map((x) => ({ value: x.id, label: x.hostname }))} />
        </div>
      ) : null}
      {step === 1 ? (
        <section className="space-y-2">
          <h2 className="text-lg font-semibold">{t`1. Create a distribution`}</h2>
          <p className="text-sm text-fg-muted">{t`Your hostname, your origin URL and the Host header your origin expects. You keep your origin and player; the edges cache in front of them.`}</p>
          {d ? <p className="text-sm">{t`Existing distribution:`} <span className="ltr-isolate font-medium">{d.hostname}</span> → <span className="ltr-isolate">{d.origin_url}</span></p> : null}
          {write ? <DistributionForm onSaved={(x) => { setPicked(x.id); if (x.secret) setSecret(x.secret); setStep(2); }} /> : null}
          {secret ? <SecretOnce secret={secret} onDone={() => setSecret(undefined)} /> : null}
          <Button variant="secondary" onClick={() => setStep(2)} disabled={!d}>{t`Next`}</Button>
        </section>
      ) : null}
      {step === 2 && d ? (
        <section className="space-y-2">
          <h2 className="text-lg font-semibold">{t`2. Check playback through every edge`}</h2>
          <Checker key={d.id} initialURL={`https://${d.hostname}/`} />
          <Button variant="secondary" onClick={() => setStep(3)}>{t`Next`}</Button>
        </section>
      ) : null}
      {step === 3 && d ? (
        <section className="space-y-2">
          <h2 className="text-lg font-semibold">{t`3. Choose the token mode`}</h2>
          <p className="text-sm">{t`Current mode:`} <Badge tone={d.token_mode === 'none' ? 'neutral' : 'ok'}>{d.token_mode.toUpperCase()}</Badge></p>
          {write ? (
            <div className="flex flex-wrap gap-2">
              {(['none', 'hmac', 'jwt'] as TokenMode[]).map((m) => (
                <Button key={m} variant={d.token_mode === m ? 'primary' : 'secondary'} disabled={save.isPending || d.token_mode === m}
                  onClick={() => save.mutate({ id: d.id, body: { token_mode: m } }, { onSuccess: (x) => { if (x.secret) setSecret(x.secret); }, onError: (x) => setErr(errText(x)) })}>
                  {m === 'none' ? t`None` : m === 'hmac' ? t`ViewStream HMAC` : t`JWT`}
                </Button>
              ))}
            </div>
          ) : null}
          {secret ? <SecretOnce secret={secret} onDone={() => setSecret(undefined)} /> : null}
          <p className="text-sm text-fg-muted">{t`HMAC URLs look like https://host/<expires>/<signature>/path — sign the directory of the master playlist so variants and segments verify too (Distributions → Sign a URL shows one). My existing scheme: send Interhost one sample signed URL and we add an adapter so your apps need no change.`}</p>
          <Button variant="secondary" onClick={() => setStep(4)}>{t`Next`}</Button>
        </section>
      ) : null}
      {step === 4 && d ? (
        <section className="space-y-2">
          <h2 className="text-lg font-semibold">{t`4. DNS and origin allow-list`}</h2>
          <p className="text-sm">{t`Create this DNS record:`} <Copyable value={`${d.hostname}. CNAME ${d.cname_target}.`} label={t`DNS record`} /></p>
          <p className="text-sm">{t`Allow these edge addresses at your origin (and only them, if you lock it down):`} <Copyable value={d.edge_ips.join(' ')} label={t`edge addresses`} /></p>
          <StatusPanel d={d} />
          <Button variant="secondary" onClick={() => setStep(5)}>{t`Next`}</Button>
        </section>
      ) : null}
      {step === 5 && d ? (
        <section className="space-y-2">
          <h2 className="text-lg font-semibold">{t`5. Switch traffic`}</h2>
          <ul className="list-disc space-y-1 ps-5 text-sm">
            <li>{t`DNS cut-over: point your current playback hostname at ${d.cname_target} (lower the TTL a day before).`}</li>
            <li>{t`Weighted split: send a share of sessions to ${d.hostname} at your DNS or load balancer and raise it gradually.`}</li>
            <li>{t`Content steering: add ${d.hostname} as a pathway in your own steering manifest.`}</li>
          </ul>
          <div role="status" aria-live="polite" className="rounded border border-border p-3 text-sm" data-testid="traffic-seen">
            {traffic.isError ? t`Edge statistics are not available right now.` : traffic.data?.seen ? (
              <>
                <p className="flex items-center gap-2 font-medium text-ok"><CheckCircle2 className="size-4" aria-hidden /> {t`Traffic seen in the last hour`}</p>
                <ul className="mt-1">
                  {traffic.data.sites.map((s) => <li key={s.site}>{t`${s.site}: ${s.requests} requests, last at ${fmtDateTime(s.last, { locale, tz }, { timeStyle: 'short' })}`}</li>)}
                </ul>
              </>
            ) : t`Waiting for the first requests… (checked every 15 seconds)`}
          </div>
        </section>
      ) : null}
      {err ? <p role="alert" className="text-sm text-danger">{err}</p> : null}
    </div>
  );
}
